import {
  FilterXSS,
  escapeHtml,
  safeAttrValue,
  type IFilterXSSOptions,
  type IWhiteList,
} from "xss";

// Rich-text descriptions (role/project/education summaries, email
// introductions and conclusions) are edited with RichTextAIField, a TipTap
// StarterKit editor, and stored as HTML. That HTML is rendered back to the
// page via `dangerouslySetInnerHTML` — including on PUBLIC Float/Submission
// candidate pages — and some of the same content originates from CV/AI
// imports rather than the editor. The editor itself is safe (TipTap re-parses
// and drops anything StarterKit does not model), but the read-only render
// path bypasses that parse, so unsanitized imported HTML would inject
// arbitrary markup/scripts (stored XSS). Every such sink must run its HTML
// through this helper first.
//
// The allowlist mirrors what StarterKit can actually emit, so genuine
// formatting survives untouched while scripts, event handlers, styles and
// unknown tags are stripped.
//
// This deliberately uses `xss` (a string-based filter) rather than DOMPurify:
// these components render on the server too, and DOMPurify there needs jsdom,
// whose ESM-only dependencies fail to load on Vercel (Sentry FLOATS-1BB) and
// 500 the page. `xss` needs no DOM, so server and browser produce identical
// output.
const ALLOWED_TAGS = [
  "p",
  "br",
  "strong",
  "b",
  "em",
  "i",
  "u",
  "s",
  "strike",
  "del",
  "code",
  "pre",
  "blockquote",
  "ul",
  "ol",
  "li",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "a",
  "span",
  "hr",
];

const allowList: IWhiteList = Object.fromEntries(
  ALLOWED_TAGS.map((tag) => [tag, tag === "a" ? ["href", "target", "rel"] : []])
);

// Disallowed tags are removed but their text is kept — except these, whose
// content is code or otherwise never meant to be shown as text. (xss's own
// `stripIgnoreTagBody` mishandles nesting and leaves "[removed]" markers.)
const DROP_WITH_CONTENT = new Set([
  "script",
  "style",
  "iframe",
  "noscript",
  "noembed",
  "noframes",
  "template",
  "textarea",
  "title",
  "xmp",
  "svg",
  "math",
  "object",
  "select",
]);

// Only web, mail and phone links (plus relative/anchor links) are allowed —
// never javascript:, data: or any other scheme.
const ALLOWED_HREF = /^(?:https?:\/\/|mailto:|tel:|\/|\.\.?\/|#)/i;

const BASE_OPTIONS: IFilterXSSOptions = {
  allowList,
  allowCommentTag: false,
  css: false,
  safeAttrValue(tag, name, value, cssFilter) {
    // The default handler decodes entities/control characters and already
    // rejects javascript: hrefs; additionally drop the data:/ftp: schemes it
    // would otherwise let through.
    const safe = safeAttrValue(tag, name, value, cssFilter);
    if (name === "href" && !ALLOWED_HREF.test(safe)) return "";
    return safe;
  },
};

export default function sanitizeRichText(html?: string | null): string {
  if (!html) return "";

  // xss walks the input in order, so track whether we are inside a
  // DROP_WITH_CONTENT tag and swallow everything (text and tags) until it
  // closes. An unclosed one drops the rest of the input, as a browser would
  // for <script>.
  let dropDepth = 0;

  const filter = new FilterXSS({
    ...BASE_OPTIONS,
    onTag(_tag, _tagHtml, { isWhite }) {
      if (dropDepth > 0 && isWhite) return "";
      return undefined;
    },
    onIgnoreTag(tag, tagHtml, { isClosing }) {
      // Not a real tag (e.g. the "< b" in "a < b and c > d") — keep it as
      // text, which is how the browser would treat it.
      if (!/^[a-z]/i.test(tag)) return dropDepth > 0 ? "" : escapeHtml(tagHtml);
      if (DROP_WITH_CONTENT.has(tag) && !/\/\s*>$/.test(tagHtml)) {
        dropDepth = isClosing ? Math.max(0, dropDepth - 1) : dropDepth + 1;
      }
      return "";
    },
    escapeHtml(text) {
      return dropDepth > 0 ? "" : escapeHtml(text);
    },
  });

  return filter.process(html);
}
